import { AbsoluteRoutes, LocalStorageKeys } from '../enums';
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { LocalStorageService } from './localStorageServices';
import { URLS } from '../constants';
import api from './api';
import axios from 'axios';

let isRefreshing = false;
let subscribers: ((token: string) => void)[] = [];

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const access = LocalStorageService.getItem(LocalStorageKeys.AccessToken);
  if (access && config.headers) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onResponseError = async (error: AxiosError): Promise<AxiosError | AxiosResponse> => {
  if (error.response) {
    const { status, config } = error.response;

    if (status === 401) {
      const access = LocalStorageService.getItem(LocalStorageKeys.AccessToken);
      const refresh = LocalStorageService.getItem(LocalStorageKeys.RefreshToken);
      const phoneNumber = LocalStorageService.getItem(LocalStorageKeys.PhoneNumber);

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const { data } = await axios.post(
            `${URLS.API_AUTH}${URLS.REFRESH}`,
            {
              accessToken: access,
              phoneNumber: phoneNumber,
              refreshToken: refresh,
            },
            {
              headers: { Authorization: `Bearer ${access}` },
            },
          );

          const { accessToken, refreshToken } = data;
          LocalStorageService.setItem(LocalStorageKeys.AccessToken, accessToken);
          LocalStorageService.setItem(LocalStorageKeys.RefreshToken, refreshToken);

          subscribers.forEach(callback => callback(accessToken));
          subscribers = [];

          config.headers.Authorization = `Bearer ${accessToken}`;
          return axios(config);
        } catch (refreshError) {
          const uuid = LocalStorageService.getItem(LocalStorageKeys.Uuid);
          LocalStorageService.clear();
          LocalStorageService.setItem(LocalStorageKeys.Uuid, uuid);

          const publicRoutes = [AbsoluteRoutes.login, AbsoluteRoutes.signup];
          const currentPath = window.location.pathname as AbsoluteRoutes;

          if (!publicRoutes.includes(currentPath)) {
            window.location.replace(AbsoluteRoutes.login);
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise(resolve => {
          subscribers.push(token => {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(api.request(config));
          });
        });
      }
    }
  }

  return Promise.reject(error);
};

export const setupInterceptorsTo = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);
  return axiosInstance;
};